/**
 * Module dependencies.
 */

import { Button } from 'app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'app/components/ui/dialog';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { PasswordRequirements, allPasswordRulesMet } from 'app/components/pages/auth/password-requirements';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';

/**
 * Minimum password length (mirrors the backend).
 */

const MIN_LENGTH = 8;

/**
 * `Props` type.
 */

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (password: string) => Promise<unknown>;
};

/**
 * Export `PasswordDialog` component.
 *
 * Sets (or resets) a password via the provided `onSave` (the current user's, or
 * — from Administration — another user's). No current password is required.
 */

export function PasswordDialog({ open, onOpenChange, onSave }: Props): JSX.Element {
  const [translate] = useTranslation();
  const { success } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const mismatch = confirm.length > 0 && confirm !== password;
  const canSave = allPasswordRulesMet(password) && confirm === password && !isLoading;

  const reset = () => {
    setPassword('');
    setConfirm('');
  };

  const submit = async () => {
    if (!canSave) {
      return;
    }

    setIsLoading(true);

    try {
      await onSave(password);

      success({ message: translate('settings.profile.security.updated') });
      reset();
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      onOpenChange={value => {
        if (!value) {
          reset();
        }

        onOpenChange(value);
      }}
      open={open}
    >
      <DialogContent className={'sm:max-w-[420px]'}>
        <DialogHeader>
          <DialogTitle>{translate('settings.profile.security.setPassword')}</DialogTitle>

          <DialogDescription>{translate('settings.profile.security.hint', { min: MIN_LENGTH })}</DialogDescription>
        </DialogHeader>

        <div className={'space-y-4'}>
          <div className={'space-y-1.5'}>
            <Label htmlFor={'new-password'}>{translate('settings.profile.security.newPassword')}</Label>

            <Input
              autoComplete={'new-password'}
              id={'new-password'}
              onChange={event => setPassword(event.target.value)}
              type={'password'}
              value={password}
            />

            <PasswordRequirements value={password} />
          </div>

          <div className={'space-y-1.5'}>
            <Label htmlFor={'confirm-password'}>{translate('settings.profile.security.confirmPassword')}</Label>

            <Input
              autoComplete={'new-password'}
              id={'confirm-password'}
              onChange={event => setConfirm(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && submit()}
              type={'password'}
              value={confirm}
            />

            {mismatch && (
              <p className={'text-xs text-destructive'}>{translate('settings.profile.security.mismatch')}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button disabled={isLoading} onClick={() => onOpenChange(false)} type={'button'} variant={'outline'}>
            {translate('common.actions.cancel')}
          </Button>

          <Button disabled={!canSave} onClick={submit} type={'button'}>
            {isLoading ? translate('settings.profile.security.saving') : translate('settings.profile.security.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
